/**
 * User Management Hooks
 * Custom hooks for comprehensive user CRUD operations using reusable fetch hooks
 */

import { useCallback, useState } from "react";
import { toast } from "sonner";
import { API_ENDPOINTS } from "../../../config/api-endpoints";
import { useGet, usePost } from "../../../packages/hooks";
import { buildApiUrl } from "../../../packages/hooks/hook-utils";
import { selectAuthToken, useAuthStore } from "../../../store/useAuth";
import { useServerPagination } from "../../../hooks/use-server-pagination";
import { UserEntity, UserListResponse } from "../types/user.types";
import {
  handleHttpError,
  extractErrorMessage,
} from "../../../utils/error-utils";

/**
 * Users hook return type
 */
export interface UseUsersReturn {
  users: UserEntity[];
  isLoading: boolean;
  totalItems: number;
  fetchUsers: () => Promise<void>;
  handleCreateUser: (values: Partial<UserEntity>) => Promise<any>;
  handleUpdateUser: (id: string, values: Partial<UserEntity>) => Promise<any>;
  handleDeleteUser: (id: string) => Promise<boolean>;
  handleToggleActive: (id: string) => Promise<boolean>;
  // Pagination properties
  currentPage: number;
  itemsPerPage: number;
  totalPages: number;
  handlePageChange: (page: number) => void;
  handleItemsPerPageChange: (itemsPerPage: number) => void;
}

/**
 * Main hook for user management
 * Provides all CRUD operations
 * @returns User state and management functions
 */
export const useUsers = (): UseUsersReturn => {
  const [users, setUsers] = useState<UserEntity[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);

  // Get token from auth store
  const token = useAuthStore(selectAuthToken);

  // Initialize server-side pagination
  const pagination = useServerPagination(totalItems);

  // Memoize callbacks to prevent infinite loops
  const handleUsersSuccess = useCallback((data: UserListResponse) => {
    // Handle multiple possible response structures
    let userList: UserEntity[] = [];
    let totalCount = 0;

    if (data) {
      // Check for users array directly
      if (Array.isArray(data?.users)) {
        userList = data.users;
        totalCount = data?.pagination?.total || userList.length;
      }
      // Check for data.users structure
      else if (Array.isArray((data as any)?.data?.users)) {
        userList = (data as any).data.users;
        totalCount = (data as any)?.data?.pagination?.total || userList.length;
      }
      // Check if data itself is an array
      else if (Array.isArray(data)) {
        userList = data as unknown as UserEntity[];
        totalCount = userList.length;
      }
      // Check for data.data array structure
      else if (Array.isArray((data as any)?.data)) {
        userList = (data as any).data;
        totalCount = userList.length;
      }
    }

    setUsers(userList);
    setTotalItems(totalCount);
  }, []);

  const handleUsersError = useCallback(() => {
    // Error handled by the hook
    setUsers([]);
    setTotalItems(0);
  }, []);

  // Build query params without branch filter
  const queryParams = {
    ...pagination.getQueryParams,
  };

  // Initialize GET query for users list
  const usersQuery = useGet<UserListResponse>(API_ENDPOINTS.USERS.LIST, {
    queryParams,
    onSuccess: handleUsersSuccess,
    onError: handleUsersError,
  });

  // Create mutation
  const createMutation = usePost<UserEntity>(API_ENDPOINTS.USERS.LIST, {
    onSuccess: () => {
      usersQuery.refetch();
    },
    onError: () => {
      // Error handled by the hook
    },
  });

  /**
   * Fetch users list
   */
  const fetchUsers = async (): Promise<void> => {
    await usersQuery.refetch();
  };

  // Note: No manual refetch needed here - useGet automatically refetches when queryParams change
  // The queryKey in useGet includes queryParams, so React Query handles this automatically

  // TODO: Improve error handling and add proper response types
  /**
   * Create a new user
   */
  const handleCreateUser = async (
    values: Partial<UserEntity>
  ): Promise<any> => {
    try {
      const response = await createMutation.mutateAsync(values);
      return response;
    } catch (error) {
      return error;
    }
  };

  // TODO: Improve error handling and add proper response types
  /**
   * Update an existing user
   */
  const handleUpdateUser = async (
    id: string,
    values: Partial<UserEntity>
  ): Promise<any> => {
    try {
      // Validate the ID
      if (!id || typeof id !== "string") {
        throw new Error("Invalid user ID");
      }

      // Validate the token
      if (!token) {
        throw new Error("Authentication required");
      }

      // Construct the endpoint with ID
      const endpoint = `crm/users/${id}`;
      const url = buildApiUrl(endpoint);

      if (!url) {
        throw new Error("URL is not available");
      }

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify(values),
      });

      if (!response.ok) {
        await handleHttpError(response);
      }

      await usersQuery.refetch();
      return response;
    } catch (error) {
      console.error("Error updating user:", error);
      return error;
    }
  };

  /**
   * Delete a user
   */
  const handleDeleteUser = async (id: string): Promise<boolean> => {
    try {
      // Validate the token
      if (!token) {
        throw new Error("Authentication required");
      }

      // Construct endpoint without branchId
      const endpoint = API_ENDPOINTS.USERS.BY_ID(id);
      const url = buildApiUrl(endpoint);

      if (!url) {
        throw new Error("URL is not available");
      }

      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Authorization": `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        await handleHttpError(response, {
          404: "Пользователь не найден. Возможно, он уже удален.",
          409: "Конфликт данных. Возможно, пользователь используется в других местах.",
        });
      }

      await usersQuery.refetch();
      toast.success("Пользователь успешно удален");
      return true;
    } catch (error) {
      toast.error(
        extractErrorMessage(error, "Ошибка при удалении пользователя")
      );
      return false;
    }
  };

  /**
   * Toggle user active status
   */
  const handleToggleActive = async (id: string): Promise<boolean> => {
    try {
      if (!token) {
        throw new Error("Authentication required");
      }

      const endpoint = `crm/users/${id}/toggle-active`;
      const url = buildApiUrl(endpoint);

      if (!url) {
        throw new Error("URL is not available");
      }

      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Authorization": `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        await handleHttpError(response);
      }

      await usersQuery.refetch();
      toast.success("Статус пользователя успешно изменен");
      return true;
    } catch (error) {
      console.error("Error toggling user active status:", error);
      toast.error(
        extractErrorMessage(error, "Ошибка при изменении статуса пользователя")
      );
      return false;
    }
  };

  return {
    users,
    isLoading:
      usersQuery.isLoading ||
      usersQuery.isFetching ||
      createMutation.isPending,
    totalItems,
    fetchUsers,
    handleCreateUser,
    handleUpdateUser,
    handleDeleteUser,
    handleToggleActive,
    // Pagination handlers
    currentPage: pagination.currentPage,
    itemsPerPage: pagination.itemsPerPage,
    totalPages: pagination.totalPages,
    handlePageChange: pagination.handlePageChange,
    handleItemsPerPageChange: pagination.handleItemsPerPageChange,
  };
};
